import React, { Suspense, lazy, useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { CoinDataProvider } from './contexts/CoinDataContext';
import { WalletProvider } from './contexts/WalletContext';
import { SelectedChainProvider } from './contexts/SelectedChainContext';
import ErrorBoundary from './components/ErrorBoundary';
import PublicLayout from './layouts/PublicLayout/PublicLayout';
import DashboardLayout from './layouts/DashboardLayout/DashboardLayout';
import { authAPI, subscriptionAPI } from './services/api';
import LoadingAnimation from './shared/components/LoadingAnimation/LoadingAnimation';
import './shared/styles/global.css';
import './App.css';

// Community pages: eager import so forumAPI resolves (avoids Babel parse error in lazy chunk)
import CommunityHome from './apps/public/pages/community/CommunityHome';
import CategoryPosts from './apps/public/pages/community/CategoryPosts';
import PostDetail from './apps/public/pages/community/PostDetail';
import CreatePost from './apps/public/pages/community/CreatePost';
import EditPost from './apps/public/pages/community/EditPost';
import MyPosts from './apps/public/pages/community/MyPosts';

// Lazy load components - Natural/Dynamic loading (only shows loading for actual load time)
// React caches lazy loaded modules, so subsequent navigations are instant
const Home = lazy(() => import('./apps/public/pages/home/home/Home'));
const Features = lazy(() => import('./apps/public/pages/features/features/Features'));
const About = lazy(() => import('./apps/public/pages/about/about/About'));
const Contact = lazy(() => import('./apps/public/pages/contact/contact/Contact'));
const Pricing = lazy(() => import('./apps/public/pages/pricing/Pricing'));
const PrivacyPolicy = lazy(() => import('./apps/public/pages/privacy-policy/PrivacyPolicy'));
const AccountDeletionInfo = lazy(() => import('./apps/public/pages/account-deletion/AccountDeletionInfo'));
const TermsConditions = lazy(() => import('./apps/public/pages/terms-conditions/TermsConditions'));
const SubscriptionSelectionPage = lazy(() => import('./apps/public/pages/subscription-selection/SubscriptionSelectionPage'));
const Login = lazy(() => import('./apps/public/pages/login/login/Login'));
const Google2FA = lazy(() => import('./apps/public/pages/login/google-2fa/Google2FA'));
const Signup = lazy(() => import('./apps/public/pages/signup/signup/Signup'));
const DashboardHome = lazy(() => import('./apps/dashboard/pages/dashboard/Dashboard'));
const PredictionsPage = lazy(() => import('./apps/dashboard/pages/predictions/Predictions'));
const AlertsPage = lazy(() => import('./apps/dashboard/pages/alerts/Alerts'));
const CreatePredictionPage = lazy(() => import('./apps/dashboard/pages/create-prediction/CreatePrediction'));
const MLPredictionPage = lazy(() => import('./apps/dashboard/pages/ml-prediction/MLPrediction'));
const CandlePredictionPage = lazy(() => import('./apps/dashboard/pages/candle-prediction/CandlePrediction'));
const HistoricalPricePage = lazy(() => import('./apps/dashboard/pages/historical-price/HistoricalPrice'));
const CoinComparisonPage = lazy(() => import('./apps/dashboard/pages/coin-comparison/CoinComparison'));
const SubscriptionPage = lazy(() => import('./apps/dashboard/pages/subscription/Subscription'));
const SettingsPage = lazy(() => import('./apps/dashboard/pages/settings/Settings'));
const SwapPage = lazy(() => import('./apps/dashboard/pages/swap/Swap'));
const SwapHistoryPage = lazy(() => import('./apps/dashboard/pages/swap-history/SwapHistory'));
const BuySellPage = lazy(() => import('./apps/dashboard/pages/buy-sell/BuySell'));
const NotFound = lazy(() => import('./apps/public/pages/404/NotFound'));
const AccountDeleted = lazy(() => import('./apps/public/pages/account-deleted/AccountDeleted'));

// Loading component
const LoadingScreen: React.FC = () => {
  return <LoadingAnimation />;
};

// Scroll to top component - scrolls to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Instant scroll to top on route change
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Simple helper function to check subscription status with retry
interface SubscriptionStatus {
  is_active: boolean;
  trial_used?: boolean;
  status?: string;
}

const checkSubscriptionWithRetry = async (maxRetries: number = 2, delay: number = 1000): Promise<{ isActive: boolean; subscription: SubscriptionStatus | null }> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await subscriptionAPI.getStatus();
      const subscription = response.data.subscription;
      
      if (subscription?.is_active) {
        return { isActive: true, subscription };
      }
      // If subscription exists but not active, return immediately (no retry needed)
      if (subscription) {
        return { isActive: false, subscription };
      }
      // Subscription is null - retry if not last attempt
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (err) {
      // Retry on error if not last attempt
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  return { isActive: false, subscription: null };
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireSubscription?: boolean }> = ({ children, requireSubscription = true }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingSubscription, setCheckingSubscription] = useState(requireSubscription);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionStatus | null>(null);
  
  // Check for token in localStorage as fallback
  const hasToken = !!localStorage.getItem('access_token');
  
  // Debug logging
  if (import.meta.env.DEV) {
    console.log('ProtectedRoute check:', {
      isAuthenticated,
      loading,
      hasUser: !!user,
      hasToken,
      userId: user?.id,
      requireSubscription,
      checkingSubscription
    });
  }
  
  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      // Only check subscription if required and user is authenticated
      if (!requireSubscription || !isAuthenticated) {
        setCheckingSubscription(false);
        return;
      }

      // Skip subscription check for subscription-selection and subscription pages
      if (location.pathname === '/subscription-selection' || location.pathname === '/dashboard/subscription') {
        setCheckingSubscription(false);
        return;
      }

      // Check subscription with retry (handles webhook delays)
      const result = await checkSubscriptionWithRetry(2, 1000);
      setHasSubscription(result.isActive);
      setSubscriptionData(result.subscription);
      
        setCheckingSubscription(false);
    };

    if (isAuthenticated && !loading) {
      checkSubscription();
    }
  }, [isAuthenticated, loading, requireSubscription, location.pathname]);
  
  // Handle subscription expired - redirect to subscription page (not subscription-selection)
  // subscription-selection is ONLY for first-time signup
  // IMPORTANT: Only redirect after check completes (subscriptionData exists and not checking)
  useEffect(() => {
    // Don't redirect if:
    // 1. Still checking subscription
    // 2. Subscription is active
    // 3. subscriptionData is null (check hasn't completed)
    // 4. Subscription not required for this route
    if (checkingSubscription || hasSubscription || !subscriptionData || !requireSubscription) {
      return;
    }
    
    const trialUsed = subscriptionData.trial_used;
    const isSubscriptionPage = location.pathname === '/dashboard/subscription';
    const isSubscriptionSelectionPage = location.pathname === '/subscription-selection';
    
    console.log('[ProtectedRoute] Subscription check completed - redirecting if needed:', {
      hasSubscription,
      trialUsed,
      currentPath: location.pathname,
      isSubscriptionPage,
      isSubscriptionSelectionPage,
      subscriptionStatus: subscriptionData.status
    });
    
    // If subscription is not active (trial expired, payment failed, canceled, unpaid)
    // Redirect to subscription page for upgrade/payment retry
    // subscription-selection is ONLY for first-time signup
    const subscriptionStatus = subscriptionData.status;
    const isInactiveStatus = subscriptionStatus && ['past_due', 'unpaid', 'canceled', 'incomplete', 'incomplete_expired'].includes(subscriptionStatus);
    
    if ((trialUsed || isInactiveStatus) && !isSubscriptionPage) {
      console.log('[ProtectedRoute] Subscription inactive - redirecting to /dashboard/subscription', {
        trialUsed,
        status: subscriptionStatus,
        isInactiveStatus
      });
      navigate('/dashboard/subscription', { replace: true });
      return;
    }
    
    // If trial not used yet, allow subscription-selection (first-time signup only)
    if (!trialUsed && !isSubscriptionSelectionPage) {
      console.log('[ProtectedRoute] No subscription, trial not used - redirecting to /subscription-selection (first-time signup)');
      navigate('/subscription-selection', { replace: true });
      return;
    }
  }, [checkingSubscription, hasSubscription, subscriptionData, location.pathname, navigate, requireSubscription]);
  
  // If loading auth, show loading screen
  if (loading) {
    return <LoadingAnimation />;
  }
  
  // If checking subscription, show loading
  if (checkingSubscription) {
    return <LoadingAnimation />;
  }
  
  // Check authentication first
  if (!isAuthenticated && !hasToken && !user) {
    console.log('[ProtectedRoute] User not authenticated - redirecting to login');
    return <Navigate to="/login" />;
  }
  
  // If subscription is required but not active
  // IMPORTANT: Only redirect if we've completed the check (subscriptionData exists and not checking)
  // Don't redirect if still checking or if subscriptionData is null (check hasn't completed yet)
  // This prevents race conditions where redirect happens before check completes
  if (requireSubscription && !hasSubscription && subscriptionData && !checkingSubscription) {
    const trialUsed = subscriptionData.trial_used;
    const isSubscriptionPage = location.pathname === '/dashboard/subscription';
    const isSubscriptionSelectionPage = location.pathname === '/subscription-selection';
    
    console.log('[ProtectedRoute] Final check - No active subscription:', {
      trialUsed,
      currentPath: location.pathname,
      isSubscriptionPage,
      isSubscriptionSelectionPage,
      subscriptionStatus: subscriptionData.status
    });
    
    // If subscription is inactive (trial expired, payment failed, canceled, unpaid)
    // Only allow subscription page (lock other tabs)
    // subscription-selection is ONLY for first-time signup
    const subscriptionStatus = subscriptionData.status;
    const isInactiveStatus = subscriptionStatus && ['past_due', 'unpaid', 'canceled', 'incomplete', 'incomplete_expired'].includes(subscriptionStatus);
    
    if (trialUsed || isInactiveStatus) {
      if (!isSubscriptionPage) {
        console.log('[ProtectedRoute] Subscription inactive - redirecting to /dashboard/subscription', {
          trialUsed,
          status: subscriptionStatus,
          isInactiveStatus
        });
        return <Navigate to="/dashboard/subscription" replace />;
      }
      // Allow subscription page access for upgrades/payment retry
      console.log('[ProtectedRoute] Allowing access to subscription page for upgrade/payment retry');
      return <>{children}</>;
    }
    
    // Trial not used yet - allow subscription-selection (first-time signup only)
    if (!isSubscriptionSelectionPage) {
      console.log('[ProtectedRoute] Trial not used - redirecting to /subscription-selection (first-time signup)');
    return <Navigate to="/subscription-selection" replace />;
    }
    
    // Already on subscription-selection page (first-time signup)
    console.log('[ProtectedRoute] Allowing access to subscription-selection (first-time signup)');
  }
  
  // All checks passed
  return <>{children}</>;
};

// Key used by manual signup (email verification) to force redirect to subscription-selection after auth.
// Both Google signup and manual signup follow the same process: after auth, first-time users always go to
// /subscription-selection (package selection) — never skipped.
const REDIRECT_AFTER_AUTH_KEY = 'redirectAfterAuth';

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Check if we're processing OAuth callback
  const oauthCode = searchParams.get('code');
  
  if (loading) {
    return <LoadingAnimation />;
  }
  
  // Don't redirect if we're processing OAuth callback
  if (oauthCode) {
    return <>{children}</>;
  }
  
  // Authenticated user on a public page (e.g. just completed manual email verification)
  if (isAuthenticated) {
    const redirectTo = sessionStorage.getItem(REDIRECT_AFTER_AUTH_KEY);
    if (redirectTo === 'subscription-selection') {
      sessionStorage.removeItem(REDIRECT_AFTER_AUTH_KEY);
      return <Navigate to="/subscription-selection" replace />;
    }
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
};

// Google OAuth Callback Handler Component
const GoogleOAuthHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [processing, setProcessing] = useState(false);
  const processedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    
    // Handle OAuth errors from Google
    if (errorParam) {
      const errorMessage = searchParams.get('error_description') || searchParams.get('message') || 'Google authentication was cancelled or failed';
      navigate(`/login?error=google_auth_failed&message=${encodeURIComponent(errorMessage)}`, { replace: true });
      return;
    }
    
    // Only process if we have a code, we're not already processing, and we haven't processed this code before
    if (code && !processing && processedCodeRef.current !== code) {
      processedCodeRef.current = code;
      setProcessing(true);
      // Handle Google OAuth callback
      const handleGoogleCallback = async () => {
        try {
          const response = await authAPI.googleOAuthCallback(code, window.location.origin);
          
          // If user has 2FA enabled, backend will require an additional step
          if (response.data?.requires_2fa) {
            const twoFactorToken = response.data?.two_factor_token;
            if (twoFactorToken) {
              sessionStorage.setItem('google_2fa_token', twoFactorToken);
              setProcessing(false);
              navigate('/google-2fa', { replace: true });
              return;
            }
          }

          const { access, refresh, user: userData } = response.data;
          
          // Set tokens first
          localStorage.setItem('access_token', access);
          localStorage.setItem('refresh_token', refresh);
          
          // Same process as manual signup: always send to package selection (never skip)
          sessionStorage.setItem(REDIRECT_AFTER_AUTH_KEY, 'subscription-selection');
          
          if (import.meta.env.DEV) {
            console.log('GoogleOAuth: Tokens set, setting user', userData.id);
          }
          
          // Set user state - this will update isAuthenticated
          setUser(userData);
          
          // Theme will be applied by ThemeProvider when navigating to dashboard
          // Public theme is handled separately by PublicThemeProvider
          
          if (import.meta.env.DEV) {
            console.log('GoogleOAuth: User set, navigating to dashboard');
          }
          
          // Small delay to ensure state updates propagate
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check subscription with retry (handles webhook delays)
          const result = await checkSubscriptionWithRetry(2, 1000);
          const hasActiveSubscription = result.isActive;
          const subscription = result.subscription;
          
          console.log('[GoogleOAuth] Subscription check result:', {
            hasActiveSubscription,
            trialUsed: subscription?.trial_used,
            status: subscription?.status
          });
          
          setProcessing(false);
          
          // Clear redirect flag when we explicitly navigate (so PublicRoute doesn't use it later)
          sessionStorage.removeItem(REDIRECT_AFTER_AUTH_KEY);
          
          // Determine redirect based on subscription status
          if (hasActiveSubscription) {
            console.log('[GoogleOAuth] Active subscription - redirecting to dashboard');
            navigate('/dashboard', { replace: true });
          } else if (subscription?.trial_used) {
            // Trial used, subscription expired - redirect to subscription page (upgrade)
            console.log('[GoogleOAuth] Trial used, subscription expired - redirecting to /dashboard/subscription (upgrade page)');
            navigate('/dashboard/subscription', { replace: true });
          } else {
            // First-time user - redirect to subscription-selection
            console.log('[GoogleOAuth] No subscription, trial not used - redirecting to /subscription-selection (first-time signup)');
            navigate('/subscription-selection', { replace: true });
          }
        } catch (err: any) {
          // If error is "code already used", ignore it (likely duplicate call from React Strict Mode)
          const errorMessage = err.response?.data?.error || err.message || '';
          if (errorMessage.includes('already been used') || errorMessage.includes('invalid_grant')) {
            if (import.meta.env.DEV) {
              console.log('GoogleOAuth: Code already used (likely duplicate call), ignoring');
            }
            // If we already have tokens, just navigate
            const hasToken = localStorage.getItem('access_token');
            if (hasToken) {
              setProcessing(false);
              const result = await checkSubscriptionWithRetry(2, 1000);
              const hasActiveSubscription = result.isActive;
              const subscription = result.subscription;
              
              console.log('[GoogleOAuth] Retry subscription check:', {
                hasActiveSubscription,
                trialUsed: subscription?.trial_used
              });
              
              sessionStorage.removeItem(REDIRECT_AFTER_AUTH_KEY);
              if (hasActiveSubscription) {
                console.log('[GoogleOAuth] Active subscription - redirecting to dashboard');
                navigate('/dashboard', { replace: true });
              } else if (subscription?.trial_used) {
                console.log('[GoogleOAuth] Trial used - redirecting to /dashboard/subscription (upgrade page)');
                navigate('/dashboard/subscription', { replace: true });
              } else {
                console.log('[GoogleOAuth] First-time user - redirecting to /subscription-selection');
                navigate('/subscription-selection', { replace: true });
              }
              return;
            }
          }
          
          console.error('Google OAuth callback error:', err);
          // Get detailed error message
          const finalErrorMessage = errorMessage || 'Google authentication failed. Please try again.';
          // Clear processing state and redirect to login on error with detailed message
          setProcessing(false);
          navigate(`/login?error=google_auth_failed&message=${encodeURIComponent(finalErrorMessage)}`, { replace: true });
        }
      };

      handleGoogleCallback();
    }
  }, [searchParams, navigate, setUser, processing]);

  // Show loading animation during Google OAuth processing
  if (processing) {
    return <LoadingAnimation />;
  }

  return null;
};

// Component inside Router to apply theme on route changes
// Must be inside ThemeProvider to access useTheme
const ThemeRouteHandler: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { theme: contextTheme } = useTheme();
  
  useEffect(() => {
    const applyTheme = () => {
      const isDashboardRoute = location.pathname.startsWith('/dashboard');
      if (isDashboardRoute) {
        // Priority: user.theme (database) > contextTheme (fallback only if user not loaded)
        // Always prioritize database theme if user is loaded
        let currentTheme: 'light' | 'dark' = 'light';
        if (user && (user.theme === 'light' || user.theme === 'dark')) {
          // User is loaded, use database theme
          currentTheme = user.theme;
        } else if (contextTheme === 'light' || contextTheme === 'dark') {
          // User not loaded yet, use context theme as fallback
          currentTheme = contextTheme;
        }
        
        console.log('[ThemeRouteHandler] Applying theme:', currentTheme, 'path:', location.pathname, 'user:', user?.id, 'user theme:', user?.theme, 'context theme:', contextTheme);
        // Apply theme synchronously on route change
        document.documentElement.setAttribute('data-theme', currentTheme);
        
        // Also apply after a small delay to catch any timing issues
        setTimeout(() => {
          const currentDocTheme = document.documentElement.getAttribute('data-theme');
          if (currentDocTheme !== currentTheme) {
            console.log('[ThemeRouteHandler] Theme mismatch detected, reapplying:', currentTheme, 'current:', currentDocTheme);
            document.documentElement.setAttribute('data-theme', currentTheme);
          }
        }, 50);
      }
    };
    
    // Apply immediately
    applyTheme();
    
    // Also apply after a short delay to ensure it sticks
    const timeoutId = setTimeout(applyTheme, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, user, user?.theme, contextTheme]);
  
  return null;
};

// Simple Theme wrapper - uses database theme from user object
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Get theme from database (user.theme) or default to 'light'
  const currentTheme: 'light' | 'dark' = (user?.theme === 'light' || user?.theme === 'dark') 
    ? user.theme 
    : 'light';
  
  // Apply theme immediately on mount and when theme/user changes
  useEffect(() => {
    const applyTheme = () => {
      const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
      if (isDashboardRoute) {
        console.log('[ThemeWrapper] Applying theme:', currentTheme, 'path:', window.location.pathname, 'user:', user?.id);
        // Apply theme synchronously to prevent flash
        document.documentElement.setAttribute('data-theme', currentTheme);
      }
    };
    
    // Apply immediately (synchronously)
    applyTheme();
    
    // Also apply after very short delays to catch any race conditions
    const timeouts = [
      setTimeout(applyTheme, 0),
      setTimeout(applyTheme, 10),
      setTimeout(applyTheme, 50),
      setTimeout(applyTheme, 100),
      setTimeout(applyTheme, 200),
      setTimeout(applyTheme, 500)
    ];
    
    // Also check periodically to ensure theme stays applied
    const interval = setInterval(() => {
      const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
      if (isDashboardRoute) {
        const currentDocTheme = document.documentElement.getAttribute('data-theme');
        if (currentDocTheme !== currentTheme) {
          console.log('[ThemeWrapper] Theme mismatch, reapplying:', currentTheme, 'current:', currentDocTheme);
          document.documentElement.setAttribute('data-theme', currentTheme);
        }
      }
    }, 100); // Check more frequently
    
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      clearInterval(interval);
    };
  }, [currentTheme, user]);
  
  return (
    <ThemeProvider initialTheme={currentTheme}>
      {children}
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeWrapper>
          <Router>
            <WalletProvider>
            <SelectedChainProvider>
            <ThemeRouteHandler />
            <CoinDataProvider>
              <ScrollToTop />
              <GoogleOAuthHandler />
              <Suspense fallback={<LoadingScreen />}>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/features" element={<PublicLayout><Features /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/support" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
              <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
              <Route path="/account-deletion" element={<PublicLayout><AccountDeletionInfo /></PublicLayout>} />
              <Route path="/terms-conditions" element={<PublicLayout><TermsConditions /></PublicLayout>} />
              <Route path="/account-deleted" element={<PublicLayout><AccountDeleted /></PublicLayout>} />
              {/* Community / Forum */}
              <Route path="/community/new" element={<PublicLayout><CreatePost /></PublicLayout>} />
              <Route path="/community/edit/:id" element={<PublicLayout><EditPost /></PublicLayout>} />
              <Route path="/community/my-posts" element={<PublicLayout><MyPosts /></PublicLayout>} />
              <Route path="/community/c/:slug" element={<PublicLayout><CategoryPosts /></PublicLayout>} />
              <Route path="/community/p/:id" element={<PublicLayout><PostDetail /></PublicLayout>} />
              <Route path="/community" element={<PublicLayout><CommunityHome /></PublicLayout>} />
              
              {/* Auth Routes */}
              <Route path="/login" element={<PublicRoute><PublicLayout><Login /></PublicLayout></PublicRoute>} />
              <Route path="/google-2fa" element={<PublicRoute><PublicLayout><Google2FA /></PublicLayout></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><PublicLayout><Signup /></PublicLayout></PublicRoute>} />
              <Route path="/subscription-selection" element={
                <ProtectedRoute requireSubscription={false}>
                  <PublicLayout><SubscriptionSelectionPage /></PublicLayout>
                </ProtectedRoute>
              } />
              
              {/* Protected Dashboard Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout><DashboardHome /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/predictions" element={
                <ProtectedRoute>
                  <DashboardLayout><PredictionsPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/alerts" element={
                <ProtectedRoute>
                  <DashboardLayout><AlertsPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/ai-prediction" element={
                <ProtectedRoute>
                  <DashboardLayout><CreatePredictionPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/ml-prediction" element={
                <ProtectedRoute>
                  <DashboardLayout><MLPredictionPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/candle-prediction" element={
                <ProtectedRoute>
                  <DashboardLayout><CandlePredictionPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/historical-price" element={
                <ProtectedRoute>
                  <DashboardLayout><HistoricalPricePage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/coin-comparison" element={
                <ProtectedRoute>
                  <DashboardLayout><CoinComparisonPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/subscription" element={
                <ProtectedRoute requireSubscription={false}>
                  <DashboardLayout><SubscriptionPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/settings" element={
                <ProtectedRoute>
                  <DashboardLayout><SettingsPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/trade" element={
                <ProtectedRoute>
                  <DashboardLayout><SwapPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/swap-history" element={
                <ProtectedRoute>
                  <DashboardLayout><SwapHistoryPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/buy-sell" element={
                <ProtectedRoute>
                  <DashboardLayout><BuySellPage /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/swap" element={<Navigate to="/dashboard/trade" replace />} />
              
              {/* 404 - Catch all route */}
              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
              </Routes>
            </Suspense>
            </CoinDataProvider>
            </SelectedChainProvider>
            </WalletProvider>
          </Router>
        </ThemeWrapper>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
